import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Timeline } from 'primereact/timeline';
import moment from 'moment';
import useUser from '../../hooks/useUser';
import {
    listIssueReports, getServerMastersList,
    getAssignableEmployees, assignIssueReport,
    startIssueWork, pauseIssueWork, resumeIssueWork, endIssueWork, getIssueWorkHistory
} from '../../utilities/api';
import { exportToExcel } from '../../utilities/ExportService';
import '../../masters/CategoryMaster.css';
import './IssueReportsList.css';

// Toggle back on when the resolution-time dashboard should reappear — left in place
// (not deleted) so the KPI plumbing behind it doesn't need to be rebuilt later.
const SHOW_RESOLUTION_DASHBOARD = false;

const PRIORITY_OPTIONS = [
    { label: 'Low', value: 'Low' },
    { label: 'Medium', value: 'Medium' },
    { label: 'High', value: 'High' },
    { label: 'Urgent', value: 'Urgent' }
];
// Same color language pattern as STATUS_META — low-key blue up through alarm red.
const PRIORITY_META = {
    Low: { severity: 'info', color: '#0e7490', bg: '#ecfeff', border: '#a5f3fc', icon: 'pi-arrow-down' },
    Medium: { severity: 'warning', color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'pi-minus' },
    High: { severity: 'danger', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', icon: 'pi-arrow-up' },
    Urgent: { severity: 'danger', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: 'pi-exclamation-triangle' }
};

// Fixed pause-reason list — must mirror Support::PAUSE_REASONS on the backend, which
// validates against the same set.
const PAUSE_REASONS = ['Waiting on client', 'Blocked / Dependency', 'End of shift', 'Lunch / Break', 'Technical issue', 'Other'];

const WORK_STATUS_META = {
    'Not Started': { severity: 'secondary', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', icon: 'pi-circle' },
    'In Progress': { severity: 'warning', color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'pi-play-circle' },
    Paused: { severity: 'danger', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: 'pi-pause-circle' },
    Completed: { severity: 'success', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: 'pi-check-circle' }
};
const WORK_STATUS_OPTIONS = Object.keys(WORK_STATUS_META).map((k) => ({ label: k, value: k }));

const AVATAR_PALETTE = ['#5b2fa0', '#0e7490', '#b45309', '#be123c', '#166534', '#4338ca'];
const avatarColor = (seed) => {
    const s = String(seed || '');
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
    return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};
const initials = (name) => {
    const parts = String(name || '?').trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

const fmtDateTime = (v) => (v && moment(v).isValid() ? moment(v).format('DD-MM-YYYY, hh:mm A') : '—');
const toISODate = (d) => (d ? moment(d).format('YYYY-MM-DD') : '');

// Renders a seconds count as the coarsest two units that still read at a glance
// (e.g. "2d 4h", "5h 12m", "38m") — matches how the tracking column already reads.
const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return '—';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return mins ? `${hours}h ${mins}m` : `${hours}h`;
    }
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return hours ? `${days}d ${hours}h` : `${days}d`;
};

// The backend's work_total_worked_seconds is only as fresh as the last fetch — while
// a row is "In Progress" this tops it up with the still-running period so the on-screen
// timer ticks in real time instead of freezing until the next reload.
const liveWorkedSeconds = (row) => {
    const base = row.work_total_worked_seconds || 0;
    if (row.work_status === 'In Progress' && row.work_current_period_started_at) {
        const running = Math.floor((Date.now() - moment(row.work_current_period_started_at).valueOf()) / 1000);
        return base + Math.max(0, running);
    }
    return base;
};

export default function IssueReportsList() {
    const toast = useRef(null);
    const { userRole, userid, username } = useUser();
    const isGspos = (userRole || '').toUpperCase() === 'GSPOS';

    const [reports, setReports] = useState([]);
    const [workStatusCounts, setWorkStatusCounts] = useState({});
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);

    // Drives both the dashboard tiles and the "Status" filter — based on the Work
    // Tracking lifecycle (Not Started/In Progress/Paused/Completed), not the ticket's
    // Open/In Progress/Resolved/Closed status (that's still editable per-issue from the
    // Details dialog, just no longer the thing the dashboard summarizes).
    const [workStatusFilter, setWorkStatusFilter] = useState(null);
    const [clientFilter, setClientFilter] = useState(null);
    const [branchFilter, setBranchFilter] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [search, setSearch] = useState('');

    const [detailRow, setDetailRow] = useState(null);

    const [employees, setEmployees] = useState([]);
    const [assignRow, setAssignRow] = useState(null);
    const [assignForm, setAssignForm] = useState({ priority: null, assigned_to_userid: null });
    const [assignSaving, setAssignSaving] = useState(false);

    const [workRow, setWorkRow] = useState(null);
    const [workHistory, setWorkHistory] = useState(null);
    const [workLoading, setWorkLoading] = useState(false);
    const [workActionLoading, setWorkActionLoading] = useState(false);
    const [pausePromptOpen, setPausePromptOpen] = useState(false);
    const [pauseReason, setPauseReason] = useState(null);

    // Ticks once a second so any "In Progress" row's live elapsed timer (list + dialog)
    // stays current without polling the server.
    const [, setTick] = useState(0);
    useEffect(() => {
        const iv = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(iv);
    }, []);

    const filteredBranches = useMemo(() => {
        if (!clientFilter) return branches;
        return branches.filter((b) => String(b.client_id) === String(clientFilter));
    }, [branches, clientFilter]);

    // Work-status tile/filter is applied client-side against the already-fetched set
    // (server-side filters — client/branch/date/search — still round-trip normally).
    const displayedReports = useMemo(() => {
        if (!workStatusFilter) return reports;
        return reports.filter((r) => (r.work_status || 'Not Started') === workStatusFilter);
    }, [reports, workStatusFilter]);

    useEffect(() => {
        if (!isGspos) return;
        (async () => {
            try {
                const [clientRows, branchRows] = await Promise.all([getServerMastersList('client_creation'), getServerMastersList('branch_creation')]);
                setClients((Array.isArray(clientRows) ? clientRows : []).map((c) => ({ label: c.client_name, value: String(c.client_id) })));
                setBranches((Array.isArray(branchRows) ? branchRows : []).map((b) => ({ label: b.branch_name, value: String(b.branch_id), client_id: String(b.client_id) })));
            } catch {
                // Client/Branch filters are a convenience for GSPOS — silently skip if the lookup fails.
            }
        })();
        (async () => {
            try {
                const res = await getAssignableEmployees();
                setEmployees(Array.isArray(res?.employees) ? res.employees : []);
            } catch {
                // The Assign dialog just shows an empty list if this fails — non-fatal.
            }
        })();
    }, [isGspos]);

    const computeWorkStatusCounts = (rows) => {
        const counts = { 'Not Started': 0, 'In Progress': 0, Paused: 0, Completed: 0 };
        rows.forEach((r) => { const s = r.work_status || 'Not Started'; if (counts[s] !== undefined) counts[s] += 1; });
        setWorkStatusCounts(counts);
    };

    const loadReports = async () => {
        setLoading(true);
        try {
            const basePayload = {
                rolename: userRole || '',
                userid: userid || '',
                client_id: isGspos ? clientFilter || '' : '',
                branch_id: isGspos ? branchFilter || '' : '',
                date_from: toISODate(dateRange?.[0]),
                date_to: toISODate(dateRange?.[1] || dateRange?.[0]),
                search: search.trim(),
                status: ''
            };
            // The work-status tile/filter is applied client-side (see displayedReports),
            // so a single fetch of everything matching the server-side filters is enough.
            const res = await listIssueReports(basePayload);
            setReports(res?.reports || []);
            computeWorkStatusCounts(res?.reports || []);
            setStats(res?.stats || null);
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Load Failed', detail: 'Could not load raised issues.' });
        } finally {
            setLoading(false);
        }
    };

    // Debounced re-fetch on any server-side filter change (search included) — mirrors
    // the Product List pattern. workStatusFilter is deliberately excluded: it's applied
    // client-side against the already-fetched set, so it doesn't need a round trip.
    useEffect(() => {
        const t = setTimeout(() => { loadReports(); }, 350);
        return () => clearTimeout(t);
    }, [clientFilter, branchFilter, dateRange, search, userRole, userid]);

    const handleResetFilters = () => {
        setWorkStatusFilter(null);
        setClientFilter(null);
        setBranchFilter(null);
        setDateRange(null);
        setSearch('');
    };

    // ── Assignment (employee + priority) ──
    const openAssignDialog = (row) => {
        setAssignForm({ priority: row.priority || null, assigned_to_userid: row.assigned_to_userid || null });
        setAssignRow(row);
    };

    const saveAssignment = async () => {
        if (!assignRow) return;
        setAssignSaving(true);
        try {
            const employee = employees.find((e) => String(e.userId) === String(assignForm.assigned_to_userid));
            await assignIssueReport({
                id: assignRow.id,
                priority: assignForm.priority || '',
                assigned_to_userid: assignForm.assigned_to_userid || '',
                assigned_to_username: employee?.username || '',
                assigned_by: username || userid || ''
            });
            setReports((prev) => prev.map((r) => (r.id === assignRow.id ? {
                ...r,
                priority: assignForm.priority || null,
                assigned_to_userid: assignForm.assigned_to_userid || null,
                assigned_to_username: employee?.username || null,
                assigned_by: username || userid || null,
                assigned_at: new Date().toISOString()
            } : r)));
            toast.current?.show({ severity: 'success', summary: 'Assignment Saved', detail: employee ? `Assigned to ${employee.username}` : 'Priority updated' });
            setAssignRow(null);
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Save Failed', detail: 'Could not save the assignment.' });
        } finally {
            setAssignSaving(false);
        }
    };

    // ── Work tracking (start / pause / resume / end) ──
    const loadWorkHistory = async (issueId) => {
        setWorkLoading(true);
        try {
            const res = await getIssueWorkHistory({ issue_report_id: issueId });
            setWorkHistory({ tracking: res?.tracking || null, pauses: res?.pauses || [] });
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Load Failed', detail: 'Could not load work-tracking history.' });
        } finally {
            setWorkLoading(false);
        }
    };

    const openWorkDialog = (row) => {
        setWorkRow(row);
        setPausePromptOpen(false);
        setPauseReason(null);
        loadWorkHistory(row.id);
    };

    // Applies a fresh work-tracking snapshot to both the open dialog and the row's
    // entry in the table, so the list stays in sync without a full reload.
    const applyWorkPatch = (issueId, patch) => {
        setReports((prev) => prev.map((r) => (r.id === issueId ? { ...r, ...patch } : r)));
        setWorkRow((prev) => (prev && prev.id === issueId ? { ...prev, ...patch } : prev));
    };

    const doStartWork = async () => {
        if (!workRow) return;
        setWorkActionLoading(true);
        try {
            const now = new Date().toISOString();
            await startIssueWork({ issue_report_id: workRow.id, started_by: username || userid || '' });
            applyWorkPatch(workRow.id, { work_status: 'In Progress', work_started_at: now, work_current_period_started_at: now, work_total_worked_seconds: 0, work_pause_count: 0, work_total_paused_seconds: 0, work_ended_at: null });
            await loadWorkHistory(workRow.id);
            toast.current?.show({ severity: 'success', summary: 'Work Started' });
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Action Failed', detail: 'Could not start work.' });
        } finally {
            setWorkActionLoading(false);
        }
    };

    const doPauseWork = async () => {
        if (!workRow || !pauseReason) return;
        setWorkActionLoading(true);
        try {
            await pauseIssueWork({ issue_report_id: workRow.id, reason: pauseReason, paused_by: username || userid || '' });
            applyWorkPatch(workRow.id, {
                work_status: 'Paused',
                work_current_period_started_at: null,
                work_total_worked_seconds: liveWorkedSeconds(workRow),
                work_pause_count: (workRow.work_pause_count || 0) + 1
            });
            await loadWorkHistory(workRow.id);
            setPausePromptOpen(false);
            setPauseReason(null);
            toast.current?.show({ severity: 'success', summary: 'Work Paused', detail: pauseReason });
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Action Failed', detail: 'Could not pause work.' });
        } finally {
            setWorkActionLoading(false);
        }
    };

    const doResumeWork = async () => {
        if (!workRow) return;
        setWorkActionLoading(true);
        try {
            const now = new Date().toISOString();
            await resumeIssueWork({ issue_report_id: workRow.id, resumed_by: username || userid || '' });
            applyWorkPatch(workRow.id, { work_status: 'In Progress', work_current_period_started_at: now });
            await loadWorkHistory(workRow.id);
            toast.current?.show({ severity: 'success', summary: 'Work Resumed' });
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Action Failed', detail: 'Could not resume work.' });
        } finally {
            setWorkActionLoading(false);
        }
    };

    const doEndWork = async () => {
        if (!workRow) return;
        setWorkActionLoading(true);
        try {
            const now = new Date().toISOString();
            await endIssueWork({ issue_report_id: workRow.id, ended_by: username || userid || '' });
            applyWorkPatch(workRow.id, { work_status: 'Completed', work_current_period_started_at: null, work_total_worked_seconds: liveWorkedSeconds(workRow), work_ended_at: now });
            await loadWorkHistory(workRow.id);
            toast.current?.show({ severity: 'success', summary: 'Work Completed' });
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Action Failed', detail: 'Could not end work.' });
        } finally {
            setWorkActionLoading(false);
        }
    };

    const exportReports = () => {
        const rows = displayedReports.map((r) => ({
            ID: r.id,
            Priority: r.priority || '',
            'Reported By': r.reported_by_username || r.reported_by_userid,
            Role: r.reported_by_role || '',
            Client: r.client_name || '',
            Branch: r.branch_name || '',
            Page: r.page_name || '',
            Status: r.status,
            'Assigned To': r.assigned_to_username || 'Unassigned',
            'Work Status': r.work_status || 'Not Started',
            'Time Worked': formatDuration(liveWorkedSeconds(r)),
            'Reported At': fmtDateTime(r.created_at),
            Description: r.description || ''
        }));
        exportToExcel(rows, 'Issue_Reports');
    };

    const totalCount = reports.length;

    const reportedByBody = (row) => {
        const name = row.reported_by_username || row.reported_by_userid || '?';
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', minWidth: 0 }}>
                <div className="issue-avatar" style={{ background: avatarColor(name) }}>{initials(name)}</div>
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                    <div style={{ fontSize: '0.68rem', color: '#6d28d9', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.reported_by_role || '—'}</div>
                    {row.reporter_email && (
                        <div style={{ fontSize: '0.68rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.reporter_email}>{row.reporter_email}</div>
                    )}
                </div>
            </div>
        );
    };

    const clientBranchBody = (row) => (
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontWeight: 600, fontSize: '0.825rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.client_name || '—'}</div>
            <div style={{ fontSize: '0.72rem', color: '#6d28d9', marginTop: '0.15rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.branch_name || '—'}</div>
        </div>
    );

    const pageBody = (row) => (
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontWeight: 600, fontSize: '0.825rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.page_name || '—'}</div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.page_path || ''}</div>
        </div>
    );

    // Combined "Assigned To" column — assignee on top, priority tag underneath.
    // Both halves open the same Assign dialog, since they're set together there.
    const assignmentBody = (row) => {
        const priorityMeta = PRIORITY_META[row.priority] || null;
        return (
            <div style={{ cursor: isGspos ? 'pointer' : 'default' }} onClick={() => isGspos && openAssignDialog(row)} title={isGspos ? 'Click to assign / set priority' : undefined}>
                {row.assigned_to_username ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                        <div className="issue-avatar" style={{ width: 28, height: 28, fontSize: '0.65rem', background: avatarColor(row.assigned_to_username) }}>{initials(row.assigned_to_username)}</div>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.assigned_to_username}</span>
                    </div>
                ) : isGspos ? (
                    <Button label="Assign" icon="pi pi-user-plus" text size="small" className="issue-assign-btn" onClick={(e) => { e.stopPropagation(); openAssignDialog(row); }} />
                ) : (
                    <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>Unassigned</span>
                )}
                <div style={{ marginTop: '0.35rem' }}>
                    {priorityMeta ? (
                        <Tag value={row.priority} severity={priorityMeta.severity} icon={`pi ${priorityMeta.icon}`} rounded style={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.02em' }} />
                    ) : (
                        <span style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>No priority</span>
                    )}
                </div>
            </div>
        );
    };

    const workBody = (row) => {
        const status = row.work_status || 'Not Started';
        const meta = WORK_STATUS_META[status];
        const worked = liveWorkedSeconds(row);
        return (
            <div className="issue-work-cell" onClick={() => openWorkDialog(row)}>
                <Tag value={status} severity={meta.severity} icon={`pi ${meta.icon}`} rounded style={{ fontWeight: 700, fontSize: '0.68rem' }} />
                {status !== 'Not Started' && (
                    <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '0.3rem', fontWeight: 700 }}>
                        <i className="pi pi-clock" style={{ fontSize: '0.65rem', marginRight: '0.25rem', color: '#94a3b8' }} />
                        {formatDuration(worked)}
                        {row.work_pause_count > 0 && <span style={{ color: '#94a3b8', fontWeight: 500 }}> · {row.work_pause_count}x paused</span>}
                    </div>
                )}
            </div>
        );
    };

    const actionBody = (row) => <Button icon="pi pi-eye" rounded text tooltip="View Details" tooltipOptions={{ position: 'top' }} onClick={() => setDetailRow(row)} />;

    return (
        <div className="issue-reports-page">
            <Toast ref={toast} />

            {/* ── Hero: title + clickable work-status tiles (each tile doubles as the status filter) ── */}
            <div className="issue-hero">
                <div className="issue-hero-main">
                    <div className="issue-hero-icon">
                        <i className="pi pi-flag-fill" />
                    </div>
                    <div>
                        <h2 className="issue-hero-title">Issues Raised</h2>
                        <p className="issue-hero-sub">{isGspos ? 'All issues reported across every client and branch' : 'Issues you have reported'}</p>
                    </div>
                </div>

                <div className="issue-stats">
                    {[{ key: null, label: 'Total', count: totalCount, color: '#5b2fa0', icon: 'pi-inbox' },
                      ...WORK_STATUS_OPTIONS.map((s) => ({ key: s.value, label: s.label, count: workStatusCounts[s.value] || 0, ...WORK_STATUS_META[s.value] }))
                    ].map((tile) => {
                        const active = workStatusFilter === tile.key;
                        return (
                            <div
                                key={tile.label}
                                className={`issue-stat${active ? ' active' : ''}`}
                                onClick={() => setWorkStatusFilter(active ? null : tile.key)}
                                title={`Filter by ${tile.label}`}
                            >
                                <div className="issue-stat-count" style={active ? { color: tile.color } : undefined}>
                                    <i className={`pi ${tile.icon}`} style={active ? { color: tile.color } : undefined} />
                                    {tile.count}
                                </div>
                                <div className="issue-stat-label">{tile.label}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Resolution-time KPI dashboard (hidden for now — see SHOW_RESOLUTION_DASHBOARD) ── */}
            {SHOW_RESOLUTION_DASHBOARD && (
                <div className="issue-kpi-section">
                    <div className="issue-kpi-heading">
                        <i className="pi pi-chart-line" />
                        <span>Resolution-Time Dashboard</span>
                    </div>
                    <div className="issue-kpi-grid">
                        <div className="issue-kpi-card">
                            <div className="issue-kpi-icon" style={{ background: '#5b2fa0' }}><i className="pi pi-stopwatch" /></div>
                            <div>
                                <div className="issue-kpi-value">{formatDuration(stats?.avg_resolution_seconds)}</div>
                                <div className="issue-kpi-label">Avg. Resolution Time</div>
                            </div>
                        </div>
                        <div className="issue-kpi-card">
                            <div className="issue-kpi-icon" style={{ background: '#0e7490' }}><i className="pi pi-reply" /></div>
                            <div>
                                <div className="issue-kpi-value">{formatDuration(stats?.avg_first_response_seconds)}</div>
                                <div className="issue-kpi-label">Avg. First Response</div>
                            </div>
                        </div>
                        <div className="issue-kpi-card">
                            <div className="issue-kpi-icon" style={{ background: '#16a34a' }}><i className="pi pi-check-circle" /></div>
                            <div>
                                <div className="issue-kpi-value">{stats?.resolved_today ?? 0}</div>
                                <div className="issue-kpi-label">Resolved Today</div>
                            </div>
                        </div>
                        <div className="issue-kpi-card">
                            <div className="issue-kpi-icon" style={{ background: '#0891b2' }}><i className="pi pi-calendar" /></div>
                            <div>
                                <div className="issue-kpi-value">{stats?.resolved_this_week ?? 0}</div>
                                <div className="issue-kpi-label">Resolved This Week</div>
                            </div>
                        </div>
                        <div className="issue-kpi-card">
                            <div className="issue-kpi-icon" style={{ background: '#d97706' }}><i className="pi pi-bolt" /></div>
                            <div style={{ minWidth: 0 }}>
                                <div className="issue-kpi-value">{formatDuration(stats?.fastest_resolution?.seconds)}</div>
                                <div className="issue-kpi-label">Fastest Resolution</div>
                                {stats?.fastest_resolution?.page_name && <div className="issue-kpi-sub" title={stats.fastest_resolution.page_name}>{stats.fastest_resolution.page_name}</div>}
                            </div>
                        </div>
                        <div className="issue-kpi-card">
                            <div className="issue-kpi-icon" style={{ background: '#dc2626' }}><i className="pi pi-hourglass" /></div>
                            <div style={{ minWidth: 0 }}>
                                <div className="issue-kpi-value">{formatDuration(stats?.slowest_resolution?.seconds)}</div>
                                <div className="issue-kpi-label">Slowest Resolution</div>
                                {stats?.slowest_resolution?.page_name && <div className="issue-kpi-sub" title={stats.slowest_resolution.page_name}>{stats.slowest_resolution.page_name}</div>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="issue-toolbar">
                <div className="issue-filter-row">
                    {isGspos && (
                        <div className="issue-field">
                            <label className="issue-field-label"><i className="pi pi-building" />Client</label>
                            <Dropdown value={clientFilter} options={clients} onChange={(e) => { setClientFilter(e.value); setBranchFilter(null); }} placeholder="All Clients" filter showClear className="w-full" />
                        </div>
                    )}
                    {isGspos && (
                        <div className="issue-field">
                            <label className="issue-field-label"><i className="pi pi-map-marker" />Branch</label>
                            <Dropdown
                                value={branchFilter}
                                options={filteredBranches}
                                onChange={(e) => setBranchFilter(e.value)}
                                placeholder={clientFilter ? 'All Branches' : 'Select a client first'}
                                disabled={!clientFilter}
                                filter
                                showClear
                                className="w-full"
                            />
                        </div>
                    )}
                    <div className="issue-field">
                        <label className="issue-field-label"><i className="pi pi-stopwatch" />Work Status</label>
                        <Dropdown
                            value={workStatusFilter}
                            options={WORK_STATUS_OPTIONS}
                            onChange={(e) => setWorkStatusFilter(e.value)}
                            placeholder="All Statuses"
                            showClear
                            className="w-full"
                            itemTemplate={(o) => <Tag value={o.label} severity={WORK_STATUS_META[o.value]?.severity || 'info'} icon={`pi ${WORK_STATUS_META[o.value]?.icon || ''}`} rounded style={{ fontWeight: 700 }} />}
                            valueTemplate={(o) => (o ? <Tag value={o.label} severity={WORK_STATUS_META[o.value]?.severity || 'info'} icon={`pi ${WORK_STATUS_META[o.value]?.icon || ''}`} rounded style={{ fontWeight: 700 }} /> : <span style={{ color: '#94a3b8' }}>All Statuses</span>)}
                        />
                    </div>
                    <div className="issue-field">
                        <label className="issue-field-label"><i className="pi pi-calendar" />Date Range</label>
                        <Calendar
                            value={dateRange}
                            onChange={(e) => setDateRange(e.value)}
                            selectionMode="range"
                            readOnlyInput
                            dateFormat="dd-mm-yy"
                            showIcon
                            placeholder="Select date range"
                            className="w-full"
                            hideOnRangeSelection
                        />
                    </div>
                    <div className="issue-filter-actions">
                        <Button label="Reset" icon="pi pi-refresh" className="issue-reset-btn" onClick={handleResetFilters} />
                    </div>
                </div>
            </div>

            <div className="issue-table-card">
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <div className="sub-masters-table-wrap issue-reports-table-wrap">
                        <DataTable
                            value={displayedReports}
                            paginator
                            rows={25}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="{first}–{last} of {totalRecords}"
                            showGridlines
                            stripedRows
                            resizableColumns
                            columnResizeMode="expand"
                            dataKey="id"
                            rowClassName={(row) => `issue-row-priority-${(row.priority || 'none').toLowerCase().replace(/\s+/g, '-')}`}
                            tableStyle={{ width: '100%', minWidth: '870px', tableLayout: 'auto' }}
                            className="p-datatable-sm sub-masters-table"
                            header={
                                <div className="issue-table-header">
                                    <div className="issue-table-heading">
                                        <i className="pi pi-list" />
                                        <span>Reports</span>
                                        <span className="issue-count-pill">{displayedReports.length} records</span>
                                        {workStatusFilter && (
                                            <span className="issue-active-filter" onClick={() => setWorkStatusFilter(null)} title="Clear status filter">
                                                {workStatusFilter} <i className="pi pi-times" />
                                            </span>
                                        )}
                                    </div>
                                    <div className="issue-table-tools">
                                        <span className="p-input-icon-left issue-search">
                                            <i className="pi pi-search" />
                                            <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search page, description, email…" className="w-full" />
                                        </span>
                                        <Button icon="pi pi-file-excel" label="Export" className="issue-export-btn" tooltip="Export to Excel" tooltipOptions={{ position: 'top' }} onClick={exportReports} disabled={!displayedReports.length} />
                                    </div>
                                </div>
                            }
                            emptyMessage={
                                <div className="issue-table-empty">
                                    <div className="issue-table-empty-icon"><i className="pi pi-inbox" /></div>
                                    <p className="issue-table-empty-title">No issues found</p>
                                    <p className="issue-table-empty-sub">Try adjusting your filters</p>
                                </div>
                            }
                        >
                            <Column field="reported_by_username" header="Reported By" body={reportedByBody} sortable style={{ width: '190px' }} />
                            <Column field="client_name" header="Client / Branch" body={clientBranchBody} sortable style={{ width: '130px' }} />
                            <Column field="page_name" header="Page" body={pageBody} sortable style={{ width: '150px' }} />
                            <Column field="assigned_to_username" header="Assigned To / Priority" body={assignmentBody} sortable style={{ width: '150px' }} />
                            <Column field="work_status" header="Work Tracking" body={workBody} sortable style={{ width: '140px' }} />
                            <Column header="Details" body={actionBody} style={{ width: '60px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        </DataTable>
                    </div>
                )}
            </div>

            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-flag-fill" />
                        <span>Issue #{detailRow?.id} — {detailRow?.page_name}</span>
                    </div>
                }
                visible={!!detailRow}
                onHide={() => setDetailRow(null)}
                style={{ width: '92vw', maxWidth: '760px' }}
                className="issue-detail-dialog"
                modal
            >
                {detailRow && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem', paddingTop: '0.5rem' }}>
                        <div className="issue-detail-grid">
                            <div>
                                <div className="issue-detail-field-label">Reported By</div>
                                <div className="issue-detail-field-value">{detailRow.reported_by_username || detailRow.reported_by_userid} <span style={{ color: '#94a3b8', fontWeight: 500 }}>({detailRow.reported_by_role || '—'})</span></div>
                            </div>
                            <div>
                                <div className="issue-detail-field-label">Reporter Email</div>
                                <div className="issue-detail-field-value"><a href={`mailto:${detailRow.reporter_email}`} style={{ color: '#0e7490', textDecoration: 'none' }}>{detailRow.reporter_email}</a></div>
                            </div>
                            <div>
                                <div className="issue-detail-field-label">Client / Branch</div>
                                <div className="issue-detail-field-value">{detailRow.client_name || '—'} <span style={{ color: '#94a3b8' }}>/</span> {detailRow.branch_name || '—'}</div>
                            </div>
                            <div>
                                <div className="issue-detail-field-label">Page</div>
                                <div className="issue-detail-field-value">{detailRow.page_name} <span style={{ color: '#94a3b8', fontWeight: 500 }}>({detailRow.page_path})</span></div>
                            </div>
                            <div>
                                <div className="issue-detail-field-label">Status</div>
                                {workBody(detailRow)}
                            </div>
                            <div>
                                <div className="issue-detail-field-label">Priority</div>
                                {detailRow.priority ? (
                                    <Tag value={detailRow.priority} severity={PRIORITY_META[detailRow.priority]?.severity || 'info'} icon={`pi ${PRIORITY_META[detailRow.priority]?.icon || ''}`} rounded style={{ fontWeight: 700 }} />
                                ) : <span style={{ color: '#94a3b8' }}>Not set</span>}
                            </div>
                            <div>
                                <div className="issue-detail-field-label">Assigned To</div>
                                <div className="issue-detail-field-value">
                                    {detailRow.assigned_to_username || <span style={{ color: '#94a3b8', fontWeight: 500 }}>Unassigned</span>}
                                    {detailRow.assigned_at && <span style={{ color: '#94a3b8', fontWeight: 500 }}> · {fmtDateTime(detailRow.assigned_at)}</span>}
                                </div>
                            </div>
                            <div>
                                <div className="issue-detail-field-label">Reported At</div>
                                <div className="issue-detail-field-value">{fmtDateTime(detailRow.created_at)}</div>
                            </div>
                            <div>
                                <div className="issue-detail-field-label">Screen Resolution</div>
                                <div className="issue-detail-field-value">{detailRow.screen_resolution || '—'}</div>
                            </div>
                        </div>
                        <div>
                            <div className="issue-detail-field-label" style={{ marginBottom: '0.4rem' }}>Description</div>
                            <div style={{ background: '#fff7ed', borderLeft: '4px solid #e11d48', borderRadius: '0 10px 10px 0', padding: '0.9rem 1.1rem', whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.6 }}>{detailRow.description}</div>
                        </div>
                        {detailRow.screenshot_url && (
                            <div>
                                <div className="issue-detail-field-label" style={{ marginBottom: '0.4rem' }}>Screenshot</div>
                                <a href={detailRow.screenshot_url} target="_blank" rel="noreferrer">
                                    <img src={detailRow.screenshot_url} alt="Reported issue screenshot" style={{ maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: 10, display: 'block' }} />
                                </a>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* ── Assign work dialog (employee + priority) ── */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user-plus" />
                        <span>Assign Issue #{assignRow?.id}</span>
                    </div>
                }
                visible={!!assignRow}
                onHide={() => setAssignRow(null)}
                style={{ width: '92vw', maxWidth: '440px' }}
                className="issue-detail-dialog"
                modal
            >
                {assignRow && (
                    <div className="flex flex-column gap-3" style={{ paddingTop: '0.5rem' }}>
                        <div className="issue-assign-summary">
                            <span style={{ fontWeight: 700 }}>{assignRow.page_name}</span>
                            <span style={{ color: '#94a3b8' }}> — {assignRow.client_name} / {assignRow.branch_name}</span>
                        </div>
                        <div className="flex flex-column">
                            <label className="mb-2 font-medium text-sm">Priority</label>
                            <Dropdown
                                value={assignForm.priority}
                                options={PRIORITY_OPTIONS}
                                onChange={(e) => setAssignForm((f) => ({ ...f, priority: e.value }))}
                                placeholder="Select priority"
                                showClear
                                className="w-full"
                                itemTemplate={(o) => <Tag value={o.label} severity={PRIORITY_META[o.value]?.severity || 'info'} icon={`pi ${PRIORITY_META[o.value]?.icon || ''}`} rounded style={{ fontWeight: 700 }} />}
                                valueTemplate={(o) => (o ? <Tag value={o.label} severity={PRIORITY_META[o.value]?.severity || 'info'} icon={`pi ${PRIORITY_META[o.value]?.icon || ''}`} rounded style={{ fontWeight: 700 }} /> : <span style={{ color: '#94a3b8' }}>Select priority</span>)}
                            />
                        </div>
                        <div className="flex flex-column">
                            <label className="mb-2 font-medium text-sm">Assign To</label>
                            <Dropdown
                                value={assignForm.assigned_to_userid}
                                options={employees.map((e) => ({ label: e.username, value: String(e.userId) }))}
                                onChange={(e) => setAssignForm((f) => ({ ...f, assigned_to_userid: e.value }))}
                                placeholder="Select employee"
                                filter
                                showClear
                                className="w-full"
                            />
                        </div>
                        <div className="flex justify-content-end gap-2" style={{ marginTop: '0.5rem' }}>
                            <Button label="Cancel" className="p-button-text" onClick={() => setAssignRow(null)} disabled={assignSaving} />
                            <Button label="Save" icon="pi pi-check" loading={assignSaving} onClick={saveAssignment} />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ── Work tracking dialog (start / pause / resume / end + history) ── */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-stopwatch" />
                        <span>Work Tracking — Issue #{workRow?.id}</span>
                    </div>
                }
                visible={!!workRow}
                onHide={() => { setWorkRow(null); setWorkHistory(null); setPausePromptOpen(false); }}
                style={{ width: '94vw', maxWidth: '620px' }}
                className="issue-detail-dialog"
                modal
            >
                {workRow && (() => {
                    const status = workRow.work_status || 'Not Started';
                    const meta = WORK_STATUS_META[status];
                    const worked = liveWorkedSeconds(workRow);
                    const events = [];
                    if (workHistory?.tracking?.work_started_at) {
                        events.push({ label: 'Started', date: workHistory.tracking.work_started_at, icon: 'pi-play', color: '#16a34a', by: workHistory.tracking.started_by });
                    }
                    (workHistory?.pauses || []).forEach((p) => {
                        events.push({ label: 'Paused', date: p.paused_at, icon: 'pi-pause', color: '#dc2626', by: p.paused_by, reason: p.reason });
                        if (p.resumed_at) events.push({ label: 'Resumed', date: p.resumed_at, icon: 'pi-play', color: '#0e7490', by: p.resumed_by });
                    });
                    if (workHistory?.tracking?.ended_at) {
                        events.push({ label: 'Completed', date: workHistory.tracking.ended_at, icon: 'pi-check', color: '#5b2fa0', by: workHistory.tracking.ended_by });
                    }
                    events.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

                    return (
                        <div className="flex flex-column gap-3" style={{ paddingTop: '0.5rem' }}>
                            <div className="issue-work-summary">
                                <Tag value={status} severity={meta.severity} icon={`pi ${meta.icon}`} rounded style={{ fontWeight: 700 }} />
                                <div className="issue-work-summary-stats">
                                    <div><span className="issue-work-summary-value">{formatDuration(worked)}</span><span className="issue-work-summary-label">Time Worked</span></div>
                                    <div><span className="issue-work-summary-value">{workRow.work_pause_count || 0}</span><span className="issue-work-summary-label">Times Paused</span></div>
                                    <div><span className="issue-work-summary-value">{formatDuration(workRow.work_total_paused_seconds || 0)}</span><span className="issue-work-summary-label">Time Paused</span></div>
                                </div>
                            </div>

                            {!pausePromptOpen ? (
                                <div className="flex gap-2 flex-wrap">
                                    {status === 'Not Started' && <Button label="Start Work" icon="pi pi-play" severity="success" loading={workActionLoading} onClick={doStartWork} />}
                                    {status === 'In Progress' && <Button label="Pause" icon="pi pi-pause" severity="warning" loading={workActionLoading} onClick={() => setPausePromptOpen(true)} />}
                                    {status === 'Paused' && <Button label="Resume" icon="pi pi-play" severity="info" loading={workActionLoading} onClick={doResumeWork} />}
                                    {(status === 'In Progress' || status === 'Paused') && <Button label="End Work" icon="pi pi-stop" severity="danger" outlined loading={workActionLoading} onClick={doEndWork} />}
                                    {status === 'Completed' && <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Work on this issue is complete.</span>}
                                </div>
                            ) : (
                                <div className="issue-pause-prompt">
                                    <label className="mb-2 font-medium text-sm">Why are you pausing?</label>
                                    <Dropdown
                                        value={pauseReason}
                                        options={PAUSE_REASONS.map((r) => ({ label: r, value: r }))}
                                        onChange={(e) => setPauseReason(e.value)}
                                        placeholder="Select a reason"
                                        className="w-full mb-2"
                                    />
                                    <div className="flex justify-content-end gap-2">
                                        <Button label="Cancel" className="p-button-text" onClick={() => { setPausePromptOpen(false); setPauseReason(null); }} disabled={workActionLoading} />
                                        <Button label="Confirm Pause" icon="pi pi-pause" severity="warning" loading={workActionLoading} disabled={!pauseReason} onClick={doPauseWork} />
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="issue-detail-field-label" style={{ marginBottom: '0.5rem' }}>Work Timeline</div>
                                {workLoading ? (
                                    <div className="flex justify-content-center" style={{ padding: '1rem' }}><ProgressSpinner style={{ width: 32, height: 32 }} /></div>
                                ) : events.length ? (
                                    <Timeline
                                        value={events}
                                        opposite={(e) => <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{fmtDateTime(e.date)}</span>}
                                        marker={(e) => <span className="issue-timeline-marker" style={{ background: e.color }}><i className={`pi ${e.icon}`} /></span>}
                                        content={(e) => (
                                            <div className="issue-timeline-content">
                                                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a' }}>{e.label}{e.by ? <span style={{ color: '#94a3b8', fontWeight: 500 }}> by {e.by}</span> : null}</div>
                                                {e.reason && <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>{e.reason}</div>}
                                            </div>
                                        )}
                                    />
                                ) : (
                                    <div style={{ color: '#94a3b8', fontSize: '0.82rem', padding: '0.5rem 0' }}>Work hasn't started on this issue yet.</div>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </Dialog>
        </div>
    );
}
